import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui';
import { Alert, Spinner } from '@/components';
import { StaticTable, type StaticColumnDef } from './utility/static-table';
import { DateCell } from './utility/date-cell/date-cell';
import type {
  OfflineRouteSurvey,
  OfflineIncidentReport,
  OfflineCustomerFeedback,
} from '../lib/db/schema';
import { SurveyType } from '../types/surveys.types';
import { useToast } from '../contexts/toast-context';
import { TOAST_TYPES } from '../constants/common.constants';
import {
  OFFLINE_SURVEY_STATUS,
  type OfflineSurveyStatus,
} from '../constants/offline.constants';
import {
  getOfflineRouteSurveys,
  getOfflineIncidentReports,
  getOfflineCustomerFeedbacks,
  getPendingSurveysCount,
  syncPendingSurveys,
  retryFailedSurvey,
} from '../services/offline-surveys.service';

interface CombinedOfflineSurvey {
  localId: string;
  type: 'Route Survey' | 'Incident Report' | 'Customer Feedback';
  createdAt: number;
  overallScore: number | null;
  enpsScore: number | null;
  commentsCount: number;
  status: OfflineSurveyStatus;
  original:
    | OfflineRouteSurvey
    | OfflineIncidentReport
    | OfflineCustomerFeedback;
}

interface OfflineSurveysProps {
  onSyncComplete?: () => void;
}

export function OfflineSurveys({ onSyncComplete }: OfflineSurveysProps) {
  const { addToast } = useToast();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [surveys, setSurveys] = useState<CombinedOfflineSurvey[]>([]);
  const [pendingCount, setPendingCount] = useState({
    routeSurveys: 0,
    incidentReports: 0,
    customerFeedbacks: 0,
    total: 0,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isSyncingRef = useRef(false);

  // Online/offline listener
  useEffect(() => {
    const updateStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  // Utilities
  const calculateOverallScore = (survey: OfflineRouteSurvey) => {
    let totalScore = 0;
    let totalQuestions = 0;

    survey.categories.forEach((cat) =>
      cat.questions.forEach((q) => {
        totalScore += q.answer.score;
        totalQuestions++;
      }),
    );

    return totalQuestions ? Math.round(totalScore / totalQuestions) : 0;
  };

  const getCommentsCount = (survey: OfflineRouteSurvey) =>
    survey.categories.reduce(
      (countCat, cat) =>
        countCat +
        cat.questions.reduce(
          (countQ, q) => (q.answer.comment ? countQ + 1 : countQ),
          0,
        ),
      0,
    ) + (survey.enpsComments ? 1 : 0);

  // Load surveys
  const loadSurveys = useCallback(async () => {
    setIsLoading(true);
    try {
      const [routeSurveys, incidentReports, customerFeedbacks, counts] =
        await Promise.all([
          getOfflineRouteSurveys(),
          getOfflineIncidentReports(),
          getOfflineCustomerFeedbacks(),
          getPendingSurveysCount(),
        ]);

      const combined: CombinedOfflineSurvey[] = [
        ...routeSurveys.map((s) => ({
          localId: s.localId,
          type: 'Route Survey' as const,
          createdAt: s.createdAt,
          overallScore: calculateOverallScore(s),
          enpsScore: s.enpsScore,
          commentsCount: getCommentsCount(s),
          status: s.status,
          original: s,
        })),
        ...incidentReports.map((f) => ({
          localId: f.localId,
          type: 'Incident Report' as const,
          createdAt: f.createdAt,
          overallScore: null,
          enpsScore: null,
          commentsCount: 1,
          status: f.status,
          original: f,
        })),
        ...customerFeedbacks.map((f) => ({
          localId: f.localId,
          type: 'Customer Feedback' as const,
          createdAt: f.createdAt,
          overallScore: null,
          enpsScore: null,
          commentsCount: 1,
          status: f.status,
          original: f,
        })),
      ].sort((a, b) => b.createdAt - a.createdAt);

      setSurveys(combined);
      setPendingCount(counts);
    } catch {
      // Only show toast if load fails
      addToast('Failed to load offline surveys.', { type: TOAST_TYPES.ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  // -----------------------
  // Sync all (manual & auto)
  // -----------------------
  const syncAll = useCallback(async () => {
    if (!isOnline || isSyncingRef.current) return;
    isSyncingRef.current = true;
    setIsSyncing(true);

    try {
      const result = await syncPendingSurveys();
      if (result.synced > 0) {
        addToast(`Synced ${result.synced} survey(s)`, {
          type: TOAST_TYPES.SUCCESS,
        });
        if (onSyncComplete) onSyncComplete();
      }
      if (result.failed > 0) {
        addToast(`Failed ${result.failed} survey(s)`, {
          type: TOAST_TYPES.ERROR,
        });
      }
      await loadSurveys();
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isOnline, addToast, loadSurveys, onSyncComplete]);

  // -----------------------
  // Retry failed survey
  // -----------------------
  const handleRetry = async (item: CombinedOfflineSurvey) => {
    try {
      const typeMap = {
        'Route Survey': SurveyType.ROUTE_SURVEY,
        'Incident Report': SurveyType.INCIDENT_REPORT,
        'Customer Feedback': SurveyType.CUSTOMER_FEEDBACK,
      };
      const success = await retryFailedSurvey(item.localId, typeMap[item.type]);
      if (success) {
        addToast(`${item.type} synced successfully`, {
          type: TOAST_TYPES.SUCCESS,
        });
        if (onSyncComplete) onSyncComplete();
        await loadSurveys();
      } else
        addToast(`Failed to sync ${item.type.toLowerCase()}`, {
          type: TOAST_TYPES.ERROR,
        });
    } catch {
      addToast(`Failed to retry ${item.type.toLowerCase()}`, {
        type: TOAST_TYPES.ERROR,
      });
    }
  };

  // -----------------------
  // Auto-sync when online
  // -----------------------
  useEffect(() => {
    if (!isOnline) return;
    syncAll(); // auto-sync on mount if online

    const handleOnline = () => syncAll();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isOnline, syncAll]);

  // Handle type click
  const handleTypeClick = () => {
    addToast(
      'You can view survey details once you have internet connectivity',
      {
        type: TOAST_TYPES.INFO,
      },
    );
  };

  // Table columns
  const columns: StaticColumnDef<CombinedOfflineSurvey>[] = [
    {
      id: 'type',
      header: 'Type',
      cell: (i) => (
        <button className="link" onClick={() => handleTypeClick()}>
          {i.type}
        </button>
      ),
    },
    {
      id: 'date',
      header: 'Date',
      cell: (i) => (
        <DateCell value={new Date(i.createdAt).toISOString()} format="date" />
      ),
    },
    {
      id: 'overallScore',
      header: 'Overall Score',
      cell: (i) => <span>{i.overallScore ?? '-'}</span>,
    },
    {
      id: 'enpsScore',
      header: 'eNPS',
      cell: (i) => <span>{i.enpsScore ?? '-'}</span>,
    },
    {
      id: 'comments',
      header: 'Comments',
      cell: (i) => <span>{i.commentsCount}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: (i) =>
        i.status === OFFLINE_SURVEY_STATUS.FAILED && (
          <Button
            size="small"
            variant="basic-secondary"
            onClick={() => handleRetry(i)}
            disabled={!isOnline}
          >
            Retry
          </Button>
        ),
    },
  ];

  if (!isLoading && surveys.length === 0) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Offline Surveys</CardTitle>
          {isOnline && pendingCount.total > 0 && (
            <Button
              onClick={syncAll}
              disabled={isSyncing}
              size="small"
              variant="basic-primary"
            >
              {isSyncing && <Spinner size="small" />} Sync All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!isOnline && pendingCount.total > 0 && (
          <>
            <Alert
              type="warning"
              text={`You are offline. Pending surveys: ${pendingCount.total}`}
            />
            <div className="mb-4"></div>
          </>
        )}
        <StaticTable
          id="offline-surveys"
          columns={columns}
          data={surveys}
          isDataLoading={isLoading}
          emptyMessage="No offline surveys"
          uppercaseHeaders={false}
        />
      </CardContent>
    </Card>
  );
}
